import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../../i18n/LanguageContext";

export const Header = () => {
  const { t, lang, changeLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/platform", label: t.nav.platform },
    { to: "/use-cases", label: t.nav.useCases },
    { to: "/industries", label: t.nav.industries },
    { to: "/company", label: t.nav.company },
    { to: "/pilot-program", label: t.nav.pilot },
    { to: "https://investor.dynamisdecisionintelligence.com", label: t.nav.investors, external: true },
  ];

  const isActive = (to) => location.pathname === to;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "bg-nb-deep/95 border-cool-dark" : "bg-nb-base border-transparent"
      }`}
      style={{ backdropFilter: scrolled ? "saturate(140%)" : "none" }}
    >
      <div className="max-w-[1240px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-6" aria-label="Primary">
          {links.map((l) => {
            const className = `text-sm font-medium whitespace-nowrap transition-colors duration-200 hover:text-cyan ${
              !l.external && isActive(l.to) ? "text-cyan" : "text-cool-light"
            }`;
            return l.external ? (
              <a key={l.to} href={l.to} target="_blank" rel="noopener noreferrer" data-testid="nav-investors" className={className}>
                {l.label}
              </a>
            ) : (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.to.replace("/", "")}`} className={className}>
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-4 ml-5 xl:ml-8">
          <div className="flex items-center border border-cool-dark rounded-sm overflow-hidden" data-testid="lang-switcher">
            {["it", "en", "de"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => changeLang(l)}
                className={`px-2.5 py-1 text-xs font-mono uppercase transition-colors duration-200 ${
                  lang === l ? "bg-cyan text-nb-deep" : "text-cool-light hover:text-white"
                }`}
                aria-pressed={lang === l}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="header-cta"
            onClick={() => navigate("/contact")}
            className="bg-cyan text-nb-deep text-sm font-semibold px-4 py-2 rounded-sm hover:bg-white transition-colors duration-200"
          >
            {t.nav.cta}
          </button>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
          aria-expanded={open}
          aria-controls="mobile-navigation"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div id="mobile-navigation" className="lg:hidden bg-nb-deep border-t border-cool-dark px-5 py-5 space-y-3" data-testid="mobile-menu">
          {links.map((l) => l.external ? (
            <a key={l.to} href={l.to} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="block text-base text-cool-light hover:text-cyan py-1" data-testid="mobile-nav-investors">
              {l.label}
            </a>
          ) : (
            <Link key={l.to} to={l.to} className="block text-base text-cool-light hover:text-cyan py-1" data-testid={`mobile-nav-${l.to.replace("/", "")}`}>
              {l.label}
            </Link>
          ))}
          <div className="flex items-center gap-3 pt-2">
            <div className="flex items-center border border-cool-dark rounded-sm overflow-hidden">
              {["it", "en", "de"].map((l) => (
                <button key={l} onClick={() => changeLang(l)} data-testid={`mobile-lang-${l}`}
                  className={`px-3 py-1.5 text-xs font-mono uppercase ${lang === l ? "bg-cyan text-nb-deep" : "text-cool-light"}`}>
                  {l}
                </button>
              ))}
            </div>
            <button onClick={() => navigate("/contact")} data-testid="mobile-header-cta"
              className="flex-1 bg-cyan text-nb-deep text-sm font-semibold px-4 py-2 rounded-sm">
              {t.nav.cta}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
